import styled from 'styled-components'
import type {SerializedError} from '@reduxjs/toolkit'
import SignInContainer from '@ui/sign-in-container/SignInContainer'
import {useNavigate, NavLink} from 'react-router-dom'
import React, {useState} from 'react'
import CompanyIcon from '~/src/assets/images/Group 968.svg?react'
import AgencyIcon from '~/src/assets/images/Group 969.svg?react'
import {BASE_ROUTES} from '~/src/routes'
import {errorText} from '../../components/auth/constants/helper-text'
import {Button} from '~/src/design-system/Primitives'

const Title = styled('h2')`
  margin: 0 0 var(--space-2);
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: var(--fw-bold);
  letter-spacing: -0.025em;
`

const SubTitle = styled('div')`
  color: var(--text-secondary);
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  margin-top: var(--space-2);
`

interface IPaperButtonContainer {
  enabled: boolean
  enableColor: string
}

const PaperButtonContainer = styled('div')<IPaperButtonContainer>`
  background-color: ${({enabled, enableColor}) => (enabled ? enableColor : 'var(--bg-card)')};
  border: 1px solid var(--border-default);
  width: 131px;
  height: 113px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  border-radius: var(--radius-sm);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`

interface ILabel {
  $enabled: boolean
}

const Label = styled('div')<ILabel>`
  font-size: 15px;
  font-weight: 700;
  color: ${({$enabled}) => ($enabled ? 'var(--white)' : 'var(--text-primary)')};
`

interface IProps {
  svg: React.ElementType<React.SVGProps<SVGSVGElement>>
  enableColor: string
  iconStyles?: React.CSSProperties
  enabled: boolean
  label: string
  callback: () => void
}

export function PaperIconButton({
  svg: SvgComponent,
  enableColor,
  iconStyles,
  enabled,
  label,
  callback,
}: IProps) {
  return (
    <PaperButtonContainer enabled={enabled} enableColor={enableColor} onClick={() => callback()}>
      <SvgComponent
        style={{
          fill: enabled ? 'white' : enableColor,
          marginTop: 'var(--space-5)',
          width: '1em',
          height: '1em',
          ...iconStyles,
        }}
      />
      <Label $enabled={enabled} style={{marginBottom: '15px'}}>
        {label}
      </Label>
    </PaperButtonContainer>
  )
}

const routeMap = {
  platformadmin: BASE_ROUTES.PLATFORM_ADMIN,
  companyadmin: BASE_ROUTES.COMPANY_ADMIN,
  agencyadmin: BASE_ROUTES.AGENCY_ADMIN,
  agency: BASE_ROUTES.AGENCY,
} as const

const defaultError: SerializedError = {
  message: errorText.serverError,
}

export default function CreateAccountPortal() {
  const navigate = useNavigate()
  const [registerCompany, setRegisterCompany] = useState(false)
  const [registerAgency, setRegisterAgency] = useState(false)
  const [continueButtonSelected, setContinueButtonSelected] = useState(false)

  const handleCompanyClick = () => {
    if (!registerCompany) {
      if (registerAgency) {
        setRegisterAgency(!registerAgency)
      }
      setRegisterCompany(!registerCompany)
    }
    setContinueButtonSelected(true)
  }

  const handleAgencyClick = () => {
    if (!registerAgency) {
      if (registerCompany) {
        setRegisterCompany(!registerCompany)
      }
      setRegisterAgency(!registerAgency)
    }
    setContinueButtonSelected(true)
  }

  return (
    <SignInContainer
      customFooter={
        <NavLink to={BASE_ROUTES.SIGNIN}>
          <span>
            Already have an account? <u>Sign in</u>
          </span>
        </NavLink>
      }>
      <Title>Create New Account</Title>
      <SubTitle>I want to register as a:</SubTitle>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          paddingTop: '75px',
          paddingBottom: '30px',
          justifyContent: 'space-between',
        }}>
        <PaperIconButton
          svg={CompanyIcon}
          enableColor="#03A9FC"
          iconStyles={{fontSize: '45px'}}
          inheritViewBox
          enabled={registerCompany}
          label="Company"
          callback={handleCompanyClick}
        />
        <PaperIconButton
          svg={AgencyIcon}
          enableColor="#03A9FC"
          iconStyles={{fontSize: '45px'}}
          viewBox="0 0 55 55"
          enabled={registerAgency}
          label="Agency"
          callback={handleAgencyClick}
        />
      </div>
      <Button
        sx={{width: '100%', marginTop: '75px'}}
        disabled={!continueButtonSelected}
        variant={continueButtonSelected ? 'contained' : 'outlined'}
        onClick={() => {
          if (registerCompany) {
            navigate(BASE_ROUTES.CREATE_COMPANY)
          } else {
            navigate(BASE_ROUTES.CREATE_AGENCY)
          }
        }}>
        Continue
      </Button>
    </SignInContainer>
  )
}
